import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import Toolbar from '@material-ui/core/Toolbar';
import Card from '@material-ui/core/Card';
import TransformerFunctions from '../../../store/transformer/functions';
import { ITransformer, TransformerMeterTypeUpperCase } from '../../../@types/model/transformer/transformer';
import MaterialTable from '../../customComponents/materialTable/Table';
import { IRootState } from '../../../@types/redux';
import TransformerAccountEditDialog from './dialog/Edit';
import TransformerDropdown from '../../customComponents/selector/transformer/TransformerSelector';
import { Tooltip, Typography } from '@material-ui/core';
import moment from 'moment';
import MeterTypeSelector from '../../customComponents/selector/transformer/MeterTypeSelector';
import Screen from '../../customComponentV2/screen/Screen';

interface ITransformerAccountListProps {
    transformers : Array<ITransformer>;
    isLoading : boolean;
}

const TransformerBankAccountList = (props : ITransformerAccountListProps) : React.ReactElement => {
    const [transformerNumber, setTransformerNumber] = useState<string | undefined>(undefined);
    const [meterType, setMeterType] = useState<TransformerMeterTypeUpperCase | ''>('');

    useEffect(() => {
        TransformerFunctions.getList();
    }, []);

    const filteredTransformers = useMemo<Array<ITransformer>>(() => {
        return props.transformers
            .filter(n => !transformerNumber || n.EPNumber === transformerNumber)
            .filter(n => !meterType || n.MeterType === meterType)
            .filter(n => n.IsActive)
            .filter(n => !!n.BankAccount);
    }, [props.transformers, transformerNumber, meterType]);

    const onEPNumberChange = (value ?: ITransformer) : void => {
        setTransformerNumber(value?.EPNumber);
    };

    const onMeterTypeChange = (newMeterType : TransformerMeterTypeUpperCase | '') : void => {
        setMeterType(newMeterType);
    };

    return (
        <Screen
            useAnimatedLoader
            isPadded={false}
            isLoading={props.isLoading}>
            <div className={'fdc flx1 p10 mh0 mw0'}>
                <Toolbar>
                    <span className={'flx1'} />
                    <div className={'fdr pt5 jcfe aic'}>
                        <div className='fdc flx1 pr5 w240'>
                            <MeterTypeSelector value={meterType} onChange={onMeterTypeChange} />
                        </div>
                        <div className='fdc flx1 pr5 w240'>
                            <TransformerDropdown
                                value={transformerNumber}
                                label='Waypoint No'
                                fullWidth
                                onChange={onEPNumberChange}
                            />
                        </div>
                    </div>
                </Toolbar>
                <Card className={'flx1 fdc mb70'}>
                    <MaterialTable<ITransformer>
                        id='transformerAccountsTable'
                        data={filteredTransformers}
                        isLoading={props.isLoading}
                        rowsPerPage={50}
                        externalSort
                        columns={[{
                            header: 'Way Point',
                            field: 'EPNumber',
                            width: 150,
                            enableSort: true,
                            enableFilter: true,
                            paddingRight: 4,
                            fixed: true,
                            renderCell: row => (
                                <div className='aic'>
                                    <TransformerAccountEditDialog 
                                        isLoading={props.isLoading}
                                        transformer={row}
                                        excludeTransformers={filteredTransformers}
                                    />
                                    <span>{row.EPNumber}</span>
                                </div>
                            ),
                        }, {
                            header: 'Meter Type',
                            field: 'MeterType',
                            width: 145,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Bank',
                            field: 'BankAccount.bank',
                            width: 155,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Branch Code',
                            field: 'BankAccount.branch',
                            width: 155,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Account No',
                            field: 'BankAccount.accountNumber',
                            width: 145,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Account Type',
                            field: 'BankAccount.accountType',
                            width: 165,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Beneficiary Descript.',
                            field: 'BankAccount.beneficiaryDescription',
                            width: 195,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Recipient Reference',
                            field: 'BankAccount.recipientReference',
                            width: 195,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Own Reference',
                            field: 'BankAccount.ownReference',
                            width: 175,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Created By',
                            field: 'CreatedByEmployee',
                            renderCell: row => (
                                <Tooltip title={row.CreatedByEmployee}>
                                    <div>
                                        {
                                            row.CreatedByName
                                        }
                                    </div>
                                </Tooltip>
                            ),                            
                            width: 175,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Created On',
                            field: 'CreatedOn',
                            renderCell: (row) => <Typography className={'fs12'}> {moment.utc(row.CreatedOn.toDate()).format('DD/MM/YYYY')}</Typography>,
                            width: 175,
                            enableSort: true,
                            enableFilter: true,
                        }]}
                    />
                </Card>
                <div className='fdr jcfe'>
                    <TransformerAccountEditDialog 
                        isLoading={props.isLoading} 
                        excludeTransformers={filteredTransformers}
                    />
                </div>
            </div>
        </Screen>
        );
    };

const mapStateToProps = (state : IRootState) => {
    return {
        transformers: state.transformer.transformers,
        isLoading: state.transformer.loading,
    };
};

export default connect(
    mapStateToProps,
)(TransformerBankAccountList);
